/**
 * AudioPlayer — plays raw PCM audio from Convorcher alongside face animation.
 *
 * Audio format: raw 16-bit signed little-endian PCM, 16 000 Hz, mono.
 * Chunks arrive uncompressed in both streaming (StreamChunk) and batch (AudioAnimation) modes.
 *
 * SentenceController calls startSentence(sentenceNum, onComplete) when it decides to play.
 * If chunks arrived before that call they are flushed immediately; if they arrive after they
 * are scheduled on the fly using gapless Web Audio API timing.
 */

import { tracePhase } from '../lib/perfProbe'

/** Ensure input is a Uint8Array view over the given buffer/array. */
function toBytes(data) {
  if (!data || data.length === 0) return new Uint8Array(0)
  return data instanceof Uint8Array ? data : new Uint8Array(data)
}

/** Convert raw PCM bytes (16-bit LE) → Float32Array samples */
function pcmBytesToFloat32(bytes) {
  const samples = new Float32Array(bytes.length >> 1)
  for (let i = 0; i < samples.length; i++) {
    let s = (bytes[i * 2 + 1] << 8) | bytes[i * 2]
    if (s >= 0x8000) s -= 0x10000
    samples[i] = s / 32768
  }
  return samples
}

class AudioPlayer {
  constructor() {
    this._ctx           = null
    this._active        = -1          // sentenceNum currently playing
    this._scheduledEnd  = 0           // AudioContext time when last scheduled buffer ends
    this._lastSource    = null        // last AudioBufferSourceNode (for onended hook)
    this._onComplete    = null        // () => void — fired when sentence audio ends
    // pending[sentenceNum] = [{ pcm: Uint8Array, done: bool }]
    this._pending       = {}
    this._rescueOff     = null        // () => void — removes the gesture-rescue listeners
    this._stateSubs     = new Set()   // callbacks for onStateChange
  }

  /** Lazy-create AudioContext (must be inside a user gesture or after one). */
  get ctx() {
    if (!this._ctx) {
      // Creating an AudioContext is a known slow first-time op (~50-150ms in
      // Chromium) because the browser initialises the audio subsystem.
      tracePhase('ap.newAudioContext', () => {
        const T0 = performance.now()
        this._ctx = new (window.AudioContext || window.webkitAudioContext)()
        const dt = performance.now() - T0
        if (dt > 5) console.log(`[AudioPlayer] AudioContext init: ${dt.toFixed(1)}ms`)
      })
      // Autoplay policy: without valid user activation the context is created
      // 'suspended' (mobile especially — a touch pointerdown does NOT count as
      // activation, and a pure-voice session may never tap the page at all).
      // Scheduled buffers on a suspended context never play and never fire
      // onended — which is what drives onSentenceCompleted — so a suspended
      // context silently wedges the whole conversation loop. Watch the state
      // and resume from the next real user gesture. iOS can also suspend or
      // 'interrupt' a running context later (route change, backgrounding), so
      // the watch stays attached for the context's whole life.
      this._ctx.addEventListener('statechange', () => this._onCtxStateChange())
      this._onCtxStateChange()
    }
    return this._ctx
  }

  /** Force AudioContext creation off the hot path (e.g. call at app init). */
  warmup() {
    return tracePhase('ap.warmup', () => {
      const ctx = this.ctx
      this.ensureRunning()
      return ctx
    })
  }

  /** Current AudioContext state, or null before first creation. */
  getState() {
    return this._ctx ? this._ctx.state : null
  }

  /**
   * Subscribe to AudioContext state changes ('running' | 'suspended' | ...).
   * Fires with the new state on every transition. Returns unsubscribe.
   */
  onStateChange(cb) {
    this._stateSubs.add(cb)
    return () => this._stateSubs.delete(cb)
  }

  /**
   * Try to resume a non-running context. Succeeds unconditionally when the
   * page holds sticky activation (desktop Chrome); on iOS it only works from
   * inside a user-gesture handler — which is exactly where the rescue
   * listeners call it from. Failure is expected pre-gesture; stay quiet.
   */
  ensureRunning() {
    const ctx = this._ctx
    if (!ctx || ctx.state === 'running') return
    ctx.resume().catch(() => { /* no activation yet — rescue listeners retry */ })
  }

  _onCtxStateChange() {
    const state = this._ctx?.state
    if (state === 'running') this._disarmRescue()
    else this._armRescue()
    for (const cb of this._stateSubs) {
      try { cb(state) } catch (e) { console.warn('[AudioPlayer] state-sub threw:', e) }
    }
  }

  // Resume from the next user gesture. pointerup/keydown are activation-
  // triggering on every platform (a touch pointerdown is NOT — activation for
  // touch lands on pointerup); pointerdown is included for the mouse case so
  // desktop recovers half a click earlier.
  _armRescue() {
    if (this._rescueOff) return
    const rescue = () => this.ensureRunning()
    const opts = { capture: true, passive: true }
    window.addEventListener('pointerdown', rescue, opts)
    window.addEventListener('pointerup', rescue, opts)
    window.addEventListener('keydown', rescue, opts)
    this._rescueOff = () => {
      window.removeEventListener('pointerdown', rescue, opts)
      window.removeEventListener('pointerup', rescue, opts)
      window.removeEventListener('keydown', rescue, opts)
    }
    console.warn('[AudioPlayer] AudioContext not running (autoplay policy) — audio resumes on next tap/keypress')
  }

  _disarmRescue() {
    this._rescueOff?.()
    this._rescueOff = null
  }

  /**
   * Reset for a new response. Stops scheduling; clears pending buffers.
   * Does NOT close the AudioContext (cannot be recreated on some browsers).
   */
  reset() {
    if (this._lastSource) {
      try { this._lastSource.onended = null } catch (_) {}
    }
    this._active       = -1
    this._scheduledEnd = 0
    this._lastSource   = null
    this._onComplete   = null
    this._pending      = {}
  }

  /**
   * Called when a streaming chunk arrives (StreamChunk message).
   * Audio bytes are raw PCM — schedule immediately or buffer for later.
   */
  addChunk(sentenceNum, rawBytes, done) {
    if (!rawBytes || rawBytes.length === 0) {
      if (done && this._active === sentenceNum) {
        // Empty terminal chunk — attach completion to the last scheduled audio source.
        // Calling _finishScheduling() immediately would fire onSentenceCompleted while
        // previously scheduled buffers are still playing.
        if (this._lastSource) {
          this._lastSource.onended = () => this._finishScheduling()
        } else {
          this._finishScheduling()  // no audio at all for this sentence
        }
      }
      return
    }
    const pcm = toBytes(rawBytes)
    if (this._active === sentenceNum) {
      this._scheduleBuffer(pcm, done)
    } else {
      if (!this._pending[sentenceNum]) this._pending[sentenceNum] = []
      this._pending[sentenceNum].push({ pcm, done })
    }
  }

  /**
   * Called when a complete AudioAnimation message arrives (batch mode).
   * Audio bytes are raw PCM — buffer or schedule immediately.
   */
  addBatch(sentenceNum, rawBytes) {
    if (!rawBytes || rawBytes.length === 0) return
    const pcm = toBytes(rawBytes)
    if (this._active === sentenceNum) {
      this._scheduleBuffer(pcm, true)
    } else {
      if (!this._pending[sentenceNum]) this._pending[sentenceNum] = []
      this._pending[sentenceNum].push({ pcm, done: true })
    }
  }

  /**
   * Called by SentenceController when it decides to play a sentence.
   * Flushes any buffered chunks and registers the onComplete callback.
   */
  startSentence(sentenceNum, onComplete) {
    this._active        = sentenceNum
    this._scheduledEnd  = 0
    this._lastSource    = null
    this._onComplete    = onComplete

    // Direct resume attempt — works wherever sticky activation suffices
    // (desktop). Where it doesn't (iOS), the gesture-rescue listeners take
    // over and this sentence's buffers play the moment the user next taps.
    this.ensureRunning()

    const chunks = this._pending[sentenceNum] ?? []
    delete this._pending[sentenceNum]

    if (chunks.length === 0) {
      // Audio arrives later — chunks will be scheduled via addChunk as they arrive.
      return
    }

    for (const { pcm, done } of chunks) {
      this._scheduleBuffer(pcm, done)
    }

    // If no chunk had done=true (terminal empty chunk arrived before startSentence),
    // wire completion to the last scheduled source now.
    if (this._lastSource && !this._lastSource.onended) {
      this._lastSource.onended = () => this._finishScheduling()
    }
  }

  // ── Internal ────────────────────────────────────────────────────────────────

  _scheduleBuffer(pcm, done) {
    if (!pcm || pcm.length < 2) {
      if (done) this._finishScheduling()
      return
    }
    tracePhase(`ap.scheduleBuffer.${pcm.length}B`, () => {
      const ctx     = this.ctx
      const samples = tracePhase(`ap.pcmToF32.${pcm.length}B`, () => pcmBytesToFloat32(pcm))
      const buffer  = tracePhase('ap.createBuffer', () => {
        const b = ctx.createBuffer(1, samples.length, 16000)
        b.getChannelData(0).set(samples)
        return b
      })
      const source  = ctx.createBufferSource()
      source.buffer = buffer
      source.connect(ctx.destination)

      const startAt         = Math.max(ctx.currentTime, this._scheduledEnd)
      source.start(startAt)
      this._scheduledEnd = startAt + buffer.duration
      this._lastSource   = source

      if (done) {
        source.onended = () => this._finishScheduling()
      }
    })
  }

  _finishScheduling() {
    const cb = this._onComplete
    this._onComplete = null
    this._active     = -1
    cb?.()
  }
}

export const audioPlayer = new AudioPlayer()

// Debug/test access — tests drive the suspended-context regression through
// this (tests/e2e/ci/audio-context-suspended-resume.spec.js).
if (typeof window !== 'undefined') window.__AUDIO_PLAYER__ = audioPlayer
